import type { TProjectSettingsTag } from '@cascade/api-client/project-settings';
import type { TProjectData, TUserProject } from '@cascade/api-client/user';
import type {
  TFeatureFlag,
  TProjectIdResolution,
  TProjectSettings,
  TProjectsScope,
} from './Projects.types.ts';
import { getTagValues, hasRequiredTagValues } from './tags/index.ts';
import { createQueryKey } from '#providers/query/index.ts';
import type { TQueryKeyPrefix } from '#providers/query/index.ts';

export const suggestedQuestionsQueryPrefix = [
  'projectSettings',
  'getSuggestedQuestions',
] as const satisfies TQueryKeyPrefix<'projectSettings'>;

export const customizableMessagesByRegionQueryPrefix = [
  'projectSettings',
  'getCustomizableMessagesByRegion',
] as const satisfies TQueryKeyPrefix<'projectSettings'>;

export const projectThemeQueryPrefix = [
  'projectSettings',
  'getProjectTheme',
] as const satisfies TQueryKeyPrefix<'projectSettings'>;

export const getProjectsQueryKey = (scope: TProjectsScope) =>
  scope === 'admin'
    ? createQueryKey('user', 'getProjectsWhereAdmin', {})
    : createQueryKey('user', 'getUserProjects', {});

export const getSettingsQueryKey = (projectId?: string) =>
  createQueryKey('projectSettings', 'getProjectPublicSettings', {
    projectId: projectId ?? '',
  });

export const getCustomizableMessagesQueryKey = (projectId?: string) =>
  createQueryKey('projectSettings', 'getCustomizableMessages', {
    project_id: projectId ?? '',
  });

export const mapToProjectData = (project: TUserProject): TProjectData => ({
  id: project.project_id,
  name: project.project_name,
});

export const resolveProjectId = (
  projectIds: string[],
  { requested, previous }: TProjectIdResolution = {},
): string | undefined => {
  if (requested !== undefined && projectIds.includes(requested)) {
    return requested;
  }

  if (previous !== undefined && projectIds.includes(previous)) {
    return previous;
  }

  return projectIds.length === 1 ? projectIds[0] : undefined;
};

export const isFeatureEnabled = (
  settings: TProjectSettings,
  flag: TFeatureFlag,
): boolean => Boolean(settings[flag]);

export const isPrimaryUser = (settings: TProjectSettings): boolean =>
  !settings.is_dependent;

export const canManageDependents = (settings: TProjectSettings): boolean =>
  isFeatureEnabled(settings, 'enable_codependents') && isPrimaryUser(settings);

export const canEditTag = (
  settings: TProjectSettings,
  tag: TProjectSettingsTag,
): boolean =>
  settings.is_admin ||
  (tag.editable && !tag.value) ||
  Boolean(tag.editable_by_users);

export const hasUnsetRequiredTags = (settings: TProjectSettings): boolean => {
  const tags = settings.session?.tags ?? [];
  const values = getTagValues(tags);

  return (
    isFeatureEnabled(settings, 'enable_tags') &&
    !hasRequiredTagValues(tags, values)
  );
};

export const hasLockedRequiredTags = (settings: TProjectSettings): boolean => {
  const tags = settings.session?.tags ?? [];
  const lockedTags = tags.filter((tag) => !canEditTag(settings, tag));
  const values = getTagValues(tags);

  return (
    isFeatureEnabled(settings, 'enable_tags') &&
    !hasRequiredTagValues(lockedTags, values)
  );
};
